// @flow

// force flow to type check this file - https://github.com/zapier/zapier/pull/31127
/* eslint-disable-next-line no-unused-vars */
import React from 'react';

import TooltipWrapper from '@zapier/design-system/src/components/display/TooltipWrapper';
import Button, {
  ButtonColors,
  type ButtonProps,
} from '@zapier/design-system/src/components/navigation/Button';
import Icon, {
  type IconName,
} from '@zapier/design-system/src/components/display/Icon';
import { isolateComponent } from '@zapier/common-utils';

import { type TooltipPosition } from '@zapier/design-system/src/components/display/Tooltip';

/** @jsx isolateComponent */

type Props = {|
  ...$Diff<
    ButtonProps,
    {
      children: $PropertyType<ButtonProps, 'children'>,
      isPadded: $PropertyType<ButtonProps, 'isPadded'>,
    }
  >,
  // ...$Exact<ButtonProps>,
  /**
   * Name of the `Icon` to render.
   */
  icon: IconName,
  /**
   * Size of the `Icon` that renders within the `Button`.
   * Typically unnecessary since the `size` prop will map
   * the desired `Icon` size in most circumstances.
   */
  iconSize?: number,
  /**
   * Indicates what the button does. Used for the `ariaLabel` on the
   * `Button` as well as the text for the `Tooltip`.
   * This prop should be supplied in most circumstances, but in cases
   * **when it's not desired be sure to pass `ariaLabel`!**
   */
  label?: string,
  /** Indicates whether to flip the `Tooltip` based on available space. */
  shouldCorrectTooltipPosition?: boolean,
  /**
   * Where to render the `Tooltip` relative to the button.
   */
  tooltipPosition: TooltipPosition,
  /** Renders the IconButton in a pill shaped format. */
  isPill?: boolean,
|};

const buttonSizeToIconSize = {
  medium: 24,
  small: 24,
  xsmall: 18,
};

/**
 * `IconButton`s are `Buttons` that render `Icon`s within them for
 * compact spaces. They accept any props that `Button` accepts, although
 * they'll always be `isSquare={true}`. Additionally, `IconButton` renders
 * a `Tooltip` which describes what the `Button` does, which is important
 * for accessibility.
 *
 * Tags: icons, core-ui
 */
const IconButton = ({
  icon,
  iconSize,
  label,
  shouldCorrectTooltipPosition,
  tooltipPosition,
  ...buttonProps
}: Props) => {
  return (
    <TooltipWrapper
      content={label}
      position={tooltipPosition}
      shouldCorrectPosition={shouldCorrectTooltipPosition}
      showTooltipOnTouchDevices={false}
    >
      {({ childProps }) => (
        <Button
          ariaLabel={label}
          {...buttonProps}
          {...childProps}
          isSquare={true}
        >
          <Icon
            canAcceptPointerEvents={false}
            isBlock={true}
            name={icon}
            size={iconSize || buttonSizeToIconSize[buttonProps.size]}
          />
        </Button>
      )}
    </TooltipWrapper>
  );
};

IconButton.defaultProps = {
  color: 'greyAlt',
  colorTheme: ButtonColors,
  size: 'small',
  tooltipPosition: 'south',
};

export default IconButton;
